import { useId, useState, useMemo } from 'react'
import type { JSX } from 'react'

import { clsx } from 'clsx'

import {
  CheckboxCard,
  Input,
  ScrollArea,
  SearchIcon,
  Text,
  CriticalIcon,
  Notice,
  Flex,
  EmptyState,
} from '@buffer-mono/popcorn'
import type { CheckedState } from '@buffer-mono/popcorn'

import ChannelListItem from '~publish/pages/Settings/ChannelGroups/components/ChannelListItem'

import {
  getMultipleChannelTypeRestrictionWarning,
  hasMultipleChannelTypeRestrictions,
} from './utils'
import type { ChannelNavChannel } from '../PublishSidebar/useChannels'

import dialogStyles from './ChannelGroupDialog.module.css'
import styles from './ChannelSelection.module.css'

export const ChannelSelection = ({
  channels,
  selectedChannelIds,
  handleOnSelectChannel,
  errorMessage,
  isMobile,
}: {
  channels: ChannelNavChannel[]
  selectedChannelIds: string[]
  handleOnSelectChannel: (channelId: string, checked: CheckedState) => void
  errorMessage: string | null
  isMobile: boolean
}): JSX.Element => {
  const [search, setSearch] = useState<string>('')
  const searchInputId = useId()
  const showSearch = channels.length >= 10

  /**
   * This is used to show a warning message when the user selects multiple of the channel types
   * which have restriction on the number of channels that can post the same content together.
   * i.e FB Groups, Twitter/X and Instagram business or personal profiles.
   */
  const restrictions = useMemo(() => {
    const selectedChannels = channels.filter((channel) =>
      selectedChannelIds.includes(channel.id),
    )
    return hasMultipleChannelTypeRestrictions(selectedChannels)
  }, [selectedChannelIds, channels])

  const filteredChannels = useMemo(() => {
    if (!search) return channels
    const term = search.toLowerCase()
    return channels.filter(
      (channel) =>
        channel.name.toLowerCase().includes(term) ||
        channel.service.toLowerCase().includes(term),
    )
  }, [channels, search])

  return (
    <>
      {showSearch && (
        <Input
          id={searchInputId}
          className={styles.searchInput}
          placeholder="Search channels"
          prefix={<SearchIcon />}
          value={search}
          onChange={(e): void => setSearch(e.target.value)}
          aria-invalid={!!errorMessage}
          aria-describedby={errorMessage ? `${searchInputId}:error` : undefined}
        />
      )}
      {errorMessage && (
        <Text
          as="span"
          size="sm"
          id={`${searchInputId}:error`}
          color="critical"
          aria-live="polite"
          role="alert"
          icon={<CriticalIcon size="xsmall" />}
          className={dialogStyles.bodyAlignment}
        >
          {errorMessage}
        </Text>
      )}
      {restrictions && (
        <Flex className={dialogStyles.bodyAlignment}>
          <Notice variant="warning" className={dialogStyles.noticeWarning}>
            {getMultipleChannelTypeRestrictionWarning(restrictions)}
          </Notice>
        </Flex>
      )}
      <ScrollArea
        gradientFade={30}
        className={clsx(styles.scrollArea, {
          [styles.scrollAreaMobile]: isMobile,
        })}
      >
        <CheckboxCard.Group
          aria-label="Select channels"
          className={styles.channelList}
        >
          {filteredChannels.length > 0 ? (
            <div className={styles.list}>
              {filteredChannels.map((channel) => (
                <ChannelListItem
                  key={channel.id}
                  channel={channel}
                  onChange={handleOnSelectChannel}
                  selected={selectedChannelIds.includes(channel.id)}
                />
              ))}
            </div>
          ) : (
            <div className={styles.empty}>
              <EmptyState>
                <EmptyState.Icon>
                  <SearchIcon />
                </EmptyState.Icon>
                <EmptyState.Heading>No channels found</EmptyState.Heading>
                <EmptyState.Description>
                  Try searching for a different channel.
                </EmptyState.Description>
              </EmptyState>
            </div>
          )}
        </CheckboxCard.Group>
      </ScrollArea>
    </>
  )
}
